import { APP_WEB_ORIGIN } from "@nfp-tracker/utils";
import type { LucideIcon } from "lucide-react";
import { Bell, CalendarCheck, CreditCard, MonitorSmartphone, Table2 } from "lucide-react";
import { ROUTES } from "./routes";

export type OnboardingStepId =
  | "log_first_observation"
  | "configure_notifications"
  | "visit_web_app"
  | "try_bulk_entry"
  | "configure_payment";

export type OnboardingStepAction =
  | { kind: "route"; route: string }
  | { kind: "external"; href: string };

export type OnboardingStep = {
  id: OnboardingStepId;
  title: string;
  description: string;
  icon: LucideIcon;
  ctaLabel: string;
  action: OnboardingStepAction;
  /**
   * Which derived signal (if any) auto-marks this step complete. Steps without an
   * autoSignal are only completed when the user explicitly marks them done (or via
   * `completeOnCta`).
   */
  autoSignal?: "notificationsEnabled" | "onWeb" | "subscriptionActive" | "hasObservation";
  /** Mark complete as soon as the CTA is used (e.g. opening the web app). */
  completeOnCta?: boolean;
  /** CTA is only usable on a desktop-sized viewport (matches the `lg` breakpoint
   * that gates the corresponding entry point elsewhere in the app). */
  requiresDesktop?: boolean;
};

/**
 * Code-configurable getting-started checklist. Add/remove/reorder steps here; the
 * persisted progress (apps/supabase user_accounts.onboarding_checklist) is keyed by
 * step id, so unknown/removed ids are simply ignored.
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "log_first_observation",
    title: "Log your first day",
    description:
      "Record today's observation — even a quick entry gets your chart started.",
    icon: CalendarCheck,
    ctaLabel: "Log an observation",
    action: { kind: "route", route: ROUTES.observations },
    autoSignal: "hasObservation",
  },
  {
    id: "configure_notifications",
    title: "Turn on reminders",
    description:
      "Enable daily reminders so you never forget to log an observation — the single biggest driver of accurate charts.",
    icon: Bell,
    ctaLabel: "Set up notifications",
    action: { kind: "route", route: `${ROUTES.settings}#notifications` },
    autoSignal: "notificationsEnabled",
  },
  {
    id: "visit_web_app",
    title: "Try the web app",
    description:
      "Open NFP Tracker in your browser to view charts and history on a bigger screen at app.nfptracker.com.",
    icon: MonitorSmartphone,
    ctaLabel: "Open the web app",
    action: { kind: "external", href: APP_WEB_ORIGIN },
    autoSignal: "onWeb",
    completeOnCta: true,
  },
  {
    id: "try_bulk_entry",
    title: "Backfill with bulk entry",
    description:
      "Log a week or two at once in a spreadsheet-style grid instead of day by day. Desktop or large-screen only — look for the grid icon in the header on Observations.",
    icon: Table2,
    ctaLabel: "Open bulk entry",
    action: { kind: "route", route: ROUTES.bulkEntry },
    completeOnCta: true,
    requiresDesktop: true,
  },
  {
    id: "configure_payment",
    title: "Set up your subscription",
    description:
      "Add a payment method so you keep full access to tracking and insights when your free trial ends.",
    icon: CreditCard,
    ctaLabel: "Manage subscription",
    action: { kind: "route", route: ROUTES.subscription },
    autoSignal: "subscriptionActive",
  },
];

export const ONBOARDING_HOME_CARD_DISMISSED_KEY = "__home_card_dismissed_at";
